import React, { useEffect, useMemo, useState } from "react";
import api from "config/axiosConfig";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { ArrowLeft, Podcast } from "lucide-react";
import { Issue } from "util/constants";
import { setDocumentTitle, setEntityOg } from "util/ogMetadataUtils";
import { useUser } from "util/UserContext";
import { Drawer, DrawerContent } from "ui/Drawer";
import EntityAvatar from "components/Entities/EntityAvatar";
import IssueFeed from "components/Issues/IssueFeed";
import IssueProfileTabs from "components/Issues/IssueProfile/IssueProfileTabs";
import LoadingSkeleton from "ui/LoadingSkeleton";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import { addRecentIssue } from "util/recentIssuesUtils";
import EntityActions from "components/Entities/EntityActions";
import EntitySentiment from "components/Alignment/EntitySentiment";
import EntitySubscription from "ui/EntitySubscription";
import { Chip } from "ui/Chip";
import { formatDate, formatRelativeTime } from "config/dayjsConfig";
import { useDrawerNavigation } from "ui/DrawerStack";
import TooltipWrapper from "ui/TooltipWrapper";
import { apiRoutes } from "util/apiRoutes";

interface IssueFeedSurfaceProps {
  /** When set, overrides the route-param cname (used by DrawerStack). */
  stackCname?: string;
  /** When set, the component renders in stacked-drawer mode (no own Drawer wrapper). */
  onStackClose?: () => void;
}

/**
 * IssueFeedSurface renders a simplified issue feed surface.
 * On mobile it appears in a right-side drawer; on desktop it renders inline.
 * When rendered by the DrawerStack (stackCname + onStackClose), it skips its
 * own Drawer wrapper and renders content directly inside the stack-provided Drawer.
 */
const IssueFeedSurface: React.FC<IssueFeedSurfaceProps> = ({ stackCname, onStackClose }) => {
  const { cname: paramCname } = useParams<{ cname: string }>();
  const cname = stackCname || paramCname;
  const isStacked = Boolean(onStackClose);
  const location = useLocation();
  const navigate = useNavigate();
  const { userIsMobile } = useUser();
  const [searchParams] = useSearchParams();
  const { openIssueFeed, openIssueProfile } = useDrawerNavigation();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [error, setError] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);

  useEffect(() => {
    setDocumentTitle(`Issue - ${cname?.replace(/_/g, " ")}`);
  }, [cname]);

  useEffect(() => {
    const fetchIssue = async () => {
      try {
        // First try to get the issue by name
        try {
          if (!cname) {
            throw new Error("Missing issue cname");
          }
          const response = await api.get(apiRoutes.issues.byName(encodeURIComponent(cname)));
          const issueData = response.data;
          const params = new URLSearchParams(location.search);
          if (params.get("upvoted") === "true") {
            issueData.user_vote = "user_upvote";
          }
          setIssue(issueData);
          setEntityOg({
            entityType: "issue",
            entity: issueData,
            urlPath: `/issue/${encodeURIComponent(issueData.cname)}`,
          });
          return;
        } catch (nameError) {
          if (!Number.isNaN(Number(cname))) {
            try {
              const idResponse = await api.get(apiRoutes.issues.byId(cname as string));
              const issueData = idResponse.data;

              // Preserve additional path segments from the current URL
              let newPath = `/issue/${encodeURIComponent(issueData.cname)}/feed`;
              const pathSegments = location.pathname.split("/");
              if (pathSegments.length > 4) {
                const additionalSegments = pathSegments.slice(4).join("/");
                newPath = `${newPath}/${additionalSegments}`;
              }
              navigate(`${newPath}${location.search}`, { replace: true });
              return;
            } catch (idError) {
              throw idError;
            }
          } else {
            throw nameError;
          }
        }
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
          setError("404: Failed to load issue feed. Please try again later.");
        } else {
          setError("Failed to load issue feed. Please try again later.");
        }
      }
    };

    fetchIssue();
  }, [cname, location.search, searchParams, navigate, location.pathname]);

  useEffect(() => {
    if (issue) {
      try {
        addRecentIssue(issue);
      } catch (error) {
        console.error("Error adding recent issue:", error);
      }
    }
  }, [issue]);

  const handleBack = () => {
    if (isStacked && onStackClose) {
      onStackClose();
    } else {
      setIsDrawerOpen(false);
      navigate(-1);
    }
  };

  /**
   * Synthetic standalone Issue for the subject side of a compound issue.
   * Returns null when the current issue is standalone.
   */
  const subjectIssue = useMemo<Issue | null>(() => {
    if (!issue || !issue.topic_id) return null;
    return {
      ...issue,
      id: issue.subject_id,
      name: (issue.subject_cname || "").replace(/_/g, " "),
      cname: issue.subject_cname || "",
      topic_id: 0,
      topic_cname: "",
      properties: issue.properties?.subject_properties || {},
    };
  }, [issue]);

  /**
   * Synthetic standalone Issue for the topic side of a compound issue.
   * Returns null when the current issue is standalone.
   */
  const topicIssue = useMemo<Issue | null>(() => {
    if (!issue || !issue.topic_id) return null;
    return {
      ...issue,
      id: issue.topic_id,
      name: (issue.topic_cname || "").replace(/_/g, " "),
      cname: issue.topic_cname || "",
      topic_id: 0,
      topic_cname: "",
      properties: issue.properties?.topic_properties || {},
    };
  }, [issue]);

  /**
   * Header for the issue feed surface.
   * Compound issues render two avatar buttons (subject + topic) linking to their standalone feeds.
   * Standalone issues render a single avatar button linking to the full IssueProfile.
   */
  const header = useMemo(() => {
    if (!issue) {
      return (
        <div className="issue-feed-surface-header flex justify-center py-4">
          <LoadingSkeleton count={1} height="h-16" width="w-16" margin="m-0" rounded="rounded-full" />
        </div>
      );
    }

    if (issue.topic_id && subjectIssue && topicIssue) {
      return (
        <div className="issue-feed-surface-header flex flex-col gap-2 py-4 px-2">
          <div className="issue-feed-surface-compound-header flex flex-row items-center gap-3 justify-center">
            <button
              type="button"
              className="issue-feed-surface-subject-button flex flex-col items-center gap-1"
              onClick={() => openIssueFeed(subjectIssue.cname)}
              aria-label={`Open ${subjectIssue.name} feed`}
            >
              <EntityAvatar
                entity={subjectIssue}
                entityType="issue"
                displayMode="avatarOnly"
                hasRing={false}
                className="w-16 h-16"
              />
              <span className="card-title text-sm hover:underline line-clamp-1 truncate-none">
                {subjectIssue.name}
              </span>
            </button>
            <span className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-lg">→</span>
            <button
              type="button"
              className="issue-feed-surface-topic-button flex flex-col items-center gap-1"
              onClick={() => openIssueFeed(topicIssue.cname)}
              aria-label={`Open ${topicIssue.name} feed`}
            >
              <EntityAvatar
                entity={topicIssue}
                entityType="issue"
                displayMode="avatarOnly"
                hasRing={false}
                className="w-16 h-16"
              />
              <span className="card-title text-sm hover:underline line-clamp-1 truncate-none">
                {topicIssue.name}
              </span>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="issue-feed-surface-header flex flex-col gap-2 py-4 px-2">
        <button
          type="button"
          className="issue-feed-surface-avatar-button w-full h-16 flex flex-col items-center justify-center"
          onClick={() => openIssueProfile(issue.cname)}
          aria-label={`Open ${issue.name} profile`}
        >
          <EntityAvatar
            entity={issue}
            entityType="issue"
            displayMode="avatarOnly"
            hasRing={false}
            className="w-16 h-16"
          />
          <div className="issue-feed-surface-title flex justify-center text-center">
            <span className="card-title text-sm">
              {issue.name}
            </span>
          </div>
        </button>
      </div>
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue, subjectIssue, topicIssue, openIssueFeed, openIssueProfile]);

  if (error) {
    return error.match(/404/)
      ? <NotFoundPage displayStyle="page" errorMessage="Sorry, we couldn't find this issue." buttonText="View Other Issues" buttonHref="/issues/new" />
      : <ServerErrorPage />;
  }

  if (!issue) {
    return (
      <>
      {userIsMobile ? (
      <>
        {cname?.includes("~") ? (
        <div className="issue-feed-surface-header-loading-skeleton-influence flex flex-col items-center justify-center py-1 gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="issue-feed-surface-header-influencer flex flex-row items-center justify-center gap-2">
              <LoadingSkeleton count={1} height="h-9" width="w-9" margin="m-0" rounded="rounded-full" />
              <LoadingSkeleton count={1} height="h-4" width="w-20" margin="m-0" rounded="rounded-full" />
            </div>
            <span className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-lg">→</span>
            <div className="issue-feed-surface-header-influenced flex flex-row items-center justify-center gap-2">
              <LoadingSkeleton count={1} height="h-9" width="w-9" margin="m-0" rounded="rounded-full" />
              <LoadingSkeleton count={1} height="h-4" width="w-20" margin="m-0" rounded="rounded-full" />
            </div>
          </div>
        </div>
        ) : (
          <div className="issue-feed-surface-header-loading-skeleton-normal flex flex-row items-center justify-center py-1 gap-2">
            <LoadingSkeleton count={1} height="h-9" width="w-9" margin="m-0" rounded="rounded-full" />
            <LoadingSkeleton count={1} height="h-4" width="w-20" margin="m-0" rounded="rounded-full" />
          </div>
        )}
      </>
      ) : (
      <>
        {cname?.includes("~") ? (
        <div className="issue-feed-surface-header-loading-skeleton-influence flex flex-col items-center justify-center py-4 gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="issue-feed-surface-header-influencer flex flex-col items-center justify-center gap-2">
              <LoadingSkeleton count={1} height="h-16" width="w-16" margin="m-0" rounded="rounded-full" />
              <LoadingSkeleton count={1} height="h-4" width="w-20" margin="m-0" rounded="rounded-full" />
            </div>
            <span className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-lg">→</span>
            <div className="issue-feed-surface-header-influenced flex flex-col items-center justify-center gap-2">
              <LoadingSkeleton count={1} height="h-16" width="w-16" margin="m-0" rounded="rounded-full" />
              <LoadingSkeleton count={1} height="h-4" width="w-20" margin="m-0" rounded="rounded-full" />
            </div>
          </div>
        </div>
        ) : (
          <div className="issue-feed-surface-header-loading-skeleton-normal flex flex-col items-center justify-center py-4 gap-2">
            <LoadingSkeleton count={1} height="h-16" width="w-16" margin="m-0" rounded="rounded-full" />
            <LoadingSkeleton count={1} height="h-4" width="w-36" margin="m-0" rounded="rounded-full" />
          </div>
        )}
        </>
      )}
      </>
    );
  }

  const feedContent = (
    <div className="issue-feed-surface-content py-0 relative">
      {!userIsMobile && header}
      {/* Tags row with sentiment, type, date, and subscription */}
      <div className="issue-feed-surface-tags flex flex-row justify-between items-center px-2">
        <div className="flex flex-row flex-wrap gap-2 items-center">
          <EntitySentiment
            entityType="issue"
            entityId={issue.id}
            variant="bar"
            className="flex-shrink-0"
          />
          {issue.properties?.type && (
            <TooltipWrapper title="AI-generated" arrow>
              <Chip
                variant="custom"
                size="sm"
                icon={<Podcast className="size-3" />}
                className="bg-eoe-entity-issue text-white"
              >
                {issue.properties.type !== "Compound" ? issue.properties.type : "Influence"}
              </Chip>
            </TooltipWrapper>
          )}
          <TooltipWrapper title={formatDate(issue.created_at)} arrow>
            <span className="card-date font-normal">
              {formatRelativeTime(issue.created_at)}
            </span>
          </TooltipWrapper>
        </div>
        <div onClick={(e) => e.stopPropagation()} className="ignore-card-click flex-shrink-0">
          <EntitySubscription
            entityId={issue.id}
            entityType="issue"
            variant="mini"
          />
        </div>
      </div>

      {/* Entity actions row */}
      <div className="issue-feed-surface-actions px-2">
        <EntityActions entity={issue} entityType="issue" />
      </div>

      <div className="issue-feed-surface-profile-tabs">
        <IssueProfileTabs issue={issue} navigationOnly />
      </div>

      <IssueFeed issue={issue} hideSort />
    </div>
  );

  const mobileHeaderContent = issue.topic_id && subjectIssue && topicIssue ? (
    <div className="issue-feed-surface-compound-mobile-header flex flex-row items-center gap-2">
      <button
        type="button"
        className="issue-feed-surface-subject-button flex items-center gap-1"
        onClick={() => openIssueFeed(subjectIssue.cname)}
        aria-label={`Open ${subjectIssue.name} feed`}
      >
        <EntityAvatar
          entity={subjectIssue}
          entityType="issue"
          displayMode="avatarOnly"
          hasRing={false}
          className="w-9 h-9"
        />
        <span className="card-title text-sm hover:underline line-clamp-1 truncate-none">
          {subjectIssue.name}
        </span>
      </button>
      <span className="text-eoe-text-heading dark:text-eoe-dark-text-heading">→</span>
      <button
        type="button"
        className="issue-feed-surface-topic-button flex items-center gap-1"
        onClick={() => openIssueFeed(topicIssue.cname)}
        aria-label={`Open ${topicIssue.name} feed`}
      >
        <EntityAvatar
          entity={topicIssue}
          entityType="issue"
          displayMode="avatarOnly"
          hasRing={false}
          className="w-9 h-9"
        />
        <span className="card-title text-sm hover:underline line-clamp-1 truncate-none">
          {topicIssue.name}
        </span>
      </button>
    </div>
  ) : (
    <button
      type="button"
      className="issue-feed-surface-avatar-button flex gap-1 items-center justify-center"
      onClick={() => openIssueProfile(issue.cname)}
      aria-label={`Open ${issue.name} profile`}
    >
      <EntityAvatar
        entity={issue}
        entityType="issue"
        displayMode="avatarOnly"
        hasRing={false}
        className="w-9 h-9"
      />
      <div className="issue-feed-surface-title flex justify-center text-center text-sm">
        <span className="card-title text-sm">{issue.name}</span>
      </div>
    </button>
  );

  if (!userIsMobile) {
    return feedContent;
  }

  /**
   * Stacked mode: the DrawerStack provides the Drawer wrapper.
   * We render the mobile header + feed content directly.
   */
  if (isStacked) {
    return (
      <>
        <div className="issue-feed-surface-mobile-header w-full flex flex-row justify-between">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="issue-feed-surface-back-button size-9 rounded-full flex items-center justify-center text-eoe-text dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
              onClick={handleBack}
              aria-label="Go back"
            >
              <ArrowLeft className="size-5" />
            </button>
            <div className="flex-1" />
          </div>
          <div className="flex flex-col items-center gap-2 py-1">
            {mobileHeaderContent}
          </div>
          <div className="size-9"/>
        </div>
        {feedContent}
      </>
    );
  }

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={(open) => {
        setIsDrawerOpen(open);
        if (!open) {
          navigate(-1);
        }
      }}
      direction="right"
      shouldScaleBackground={false}
    >
      <DrawerContent
        hideHandle
        fullScreen
        className="!left-auto !right-0 w-full focus-visible:outline-none !rounded-none !border-none bg-eoe-bg dark:bg-eoe-dark-bg"
        header={
          <div className="issue-feed-surface-mobile-header w-full flex flex-row justify-between">
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="issue-feed-surface-back-button size-9 rounded-full flex items-center justify-center text-eoe-text dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                onClick={handleBack}
                aria-label="Go back"
              >
                <ArrowLeft className="size-5" />
              </button>
              <div className="flex-1" />
            </div>
            <div className="flex flex-col items-center gap-2 py-1">
              {mobileHeaderContent}
            </div>
            <div className="size-9"/>
          </div>
        }
      >
        {feedContent}
      </DrawerContent>
    </Drawer>
  );
};

export default IssueFeedSurface;
